"use client";

import { HubIcon } from "@/app/_components/conditions/hub/hub-icons";
import type { HubIconKey } from "@/lib/conditions/hub/types";
import { AREA_SPORT_TOGGLE_ID, selectAreaSport, useAreaSport } from "./area-sport-state";

/**
 * The hero's sport toggle (G2): a chip per sport scored by three or more members, the page's own
 * sport first. Picking one switches every server-rendered panel on the page (AreaSportScope) and
 * writes `?sport=` without a navigation; H1, title and canonical stay the page's own sport. The
 * pressed look comes from the scope's style rule, so the right chip shows before hydration.
 * Inside AreaSportScope only.
 */

export type AreaToggleChip = Readonly<{
  key: string;
  label: string;
  /** In-sentence name, "bodyboarding". */
  lowerName: string;
  icon: HubIconKey;
  /** "17 beaches scored". */
  countLabel: string;
  isPrimary: boolean;
}>;

export function AreaSportToggle({
  chips,
  primary,
  areaName,
  pageTitle,
}: Readonly<{
  chips: readonly AreaToggleChip[];
  primary: string;
  /** "Cornwall". */
  areaName: string;
  /** The H1, "Cornwall surf forecast". */
  pageTitle: string;
}>) {
  const keys = chips.map((chip) => chip.key);
  const sport = useAreaSport(primary, keys);
  if (chips.length <= 1) return null;
  const primaryChip = chips.find((chip) => chip.key === primary) ?? chips[0];
  const current = chips.find((chip) => chip.key === sport) ?? primaryChip;
  const labelId = `${AREA_SPORT_TOGGLE_ID}-label`;
  return (
    <div id={AREA_SPORT_TOGGLE_ID} className="relative mt-4 md:mt-6">
      <p id={labelId} className="sr-only">Show this area for</p>
      <div
        role="group"
        aria-labelledby={labelId}
        className="-mx-4 flex gap-2 overflow-x-auto px-4 py-[3px] [mask-image:linear-gradient(90deg,#000_86%,transparent)] [scroll-padding-left:16px] [scrollbar-width:none] md:mx-0 md:flex-wrap md:overflow-visible md:px-[3px] md:[mask-image:none] [&::-webkit-scrollbar]:hidden"
      >
        {chips.map((chip) => {
          const on = chip.key === sport;
          return (
            <button
              key={chip.key}
              type="button"
              data-area-chip={chip.key}
              aria-pressed={on}
              aria-label={`${chip.label}${chip.isPrimary ? ", the main sport for this page" : ""}, ${chip.countLabel}`}
              onClick={() => selectAreaSport(chip.key, { primary })}
              className="inline-flex min-h-11 shrink-0 cursor-pointer items-center gap-[7px] whitespace-nowrap rounded-full border border-white/30 bg-[rgba(22,35,59,0.5)] py-0 pl-[11px] pr-3.5 text-[14px] font-semibold text-white transition-colors hover:border-white md:text-[14.5px]"
            >
              <span data-area-chip-icon="" className="inline-flex text-teal-light">
                <HubIcon name={chip.icon} size={18} strokeWidth={1.8} />
              </span>
              <span>{chip.label}</span>
            </button>
          );
        })}
        <span aria-hidden="true" className="w-5 shrink-0 md:hidden" />
      </div>
      <p className="sr-only" aria-live="polite">{`Showing ${current.lowerName} for ${areaName}`}</p>
      <div className="mt-2 flex min-h-11 items-center gap-2.5 md:mt-1.5">
        <p data-area-only="primary" className="text-[13px] leading-[1.35] text-[#C9D3E0] md:text-[14.5px]">
          {primaryChip.label} is the main sport here. Pick another to see its scores on this page.
        </p>
        {chips.filter((chip) => !chip.isPrimary).map((chip) => (
          <div key={chip.key} data-area-panel={chip.key} className="flex min-w-0 flex-1 items-center gap-2.5">
            <p className="min-w-0 flex-1 text-[13px] leading-[1.35] text-[#D5DCE6] md:flex-none md:text-[14.5px]">
              <strong className="font-bold text-white">Showing {chip.lowerName}.</strong> This page is the {pageTitle}.
            </p>
            <button
              type="button"
              onClick={() => selectAreaSport(primary, { primary })}
              className="min-h-11 shrink-0 cursor-pointer whitespace-nowrap border-0 bg-transparent px-0.5 text-[13px] font-bold text-teal-light underline underline-offset-[3px] md:text-[14.5px]"
            >
              Back to {primaryChip.lowerName}
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
